<template>
    <div class="history-modal-container animation-open-modal">
        <SelectModal v-if="selectModal.open" translation-base="Operator." :title="selectModal.title"
            :selected-id="selectModal.selectedId" :list="selectModal.data" :multiple="true"
            @select-item="handleSelectModalClick" @close="selectModal.open = false" />
        <div>
            <ModalHeader :isFilter="isFilter" :close="closeModal" @updateTab="(v) => (currentTab = v)" />

            <div class="filters-buttons">
                <DateTimePicker ref="dateTimePickerRefDesktop" v-model="datepicker" type="date" :range="true"
                    position="right" :week-days="setWeekDays()" :month-names="setMonths()" :block-dates="blockDates"
                    :txt-cancel-button="$t('Datepicker.Cancel')" :txt-save-button="$t('Datepicker.Save')"
                    :txt-time="$t('Datepicker.Time')" :txt-date="$t('Datepicker.Date')" :max-range-dates="7"
                    class="h-100" @input="(value) => handleDatepickerChange(value, field)">
                    <template v-slot:input>
                        <b-button class="btn-filter" @click="() => { }">
                            <CalendarIcon />
                            {{
                                datepicker
                                    ? formatRangeDate(datepicker[0], datepicker[1])
                                    : $t("Operator.Period")
                            }}
                            <ArrowDownIcon />
                        </b-button>
                    </template>
                </DateTimePicker>
                <b-button class="btn-filter" @click="handleSelectModal('shift')">
                    <TimeIcon /> {{ $t("Operator.Shift") }}
                    <ArrowDownIcon />
                </b-button>
                <b-button class="btn-filter" @click="handleSelectModal('person')">
                    <ProfileIcon /> {{ $t("Operator.Person") }}
                    <ArrowDownIcon />
                </b-button>
                <b-button v-if="currentTab === 'stops'" class="btn-filter" @click="handleSelectModal('status')">
                    <CirclePauseIcon /> Status
                    <ArrowDownIcon />
                </b-button>
            </div>

            <ul :class="['list-content', isFilter ? '--has-filters' : '']">
                <div v-if="currentTab !== 'stops' && totalPages > 1" class="pagination-container">
                    <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage = 1">
                        &laquo;
                    </button>
                    <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage--">
                        &lt;
                    </button>

                    <button v-for="page in visiblePages" :key="page" class="pagination-btn"
                        :class="{ active: page === currentPage }" @click="currentPage = page">
                        {{ page }}
                    </button>

                    <button class="pagination-btn" :disabled="currentPage === totalPages" @click="currentPage++">
                        &gt;
                    </button>
                    <button class="pagination-btn" :disabled="currentPage === totalPages"
                        @click="currentPage = totalPages">
                        &raquo;
                    </button>
                </div>

                <template v-if="currentTab === 'parameters'">
                    <template v-if="paginatedList.length > 0">
                        <ItemParameter
                            v-for="(item, index) in paginatedList"
                            :key="`param-${item.id || index}`"
                            :item="item"
                        />
                    </template>
                    <NotFound v-else />
                </template>
                <template v-else>
                    <template v-if="paginatedList.length > 0">
                        <ItemList v-for="(item, index) in paginatedList" :key="`item-${index}`" :item="item"
                            :currentTab="currentTab" :statusList="statusList" @updateList="getHistoryList" />
                    </template>
                    <NotFound v-else />
                </template>

                <div v-if="currentTab !== 'stops' && totalPages > 1" class="pagination-container">
                    <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage = 1">
                        &laquo;
                    </button>
                    <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage--">
                        &lt;
                    </button>

                    <button v-for="page in visiblePages" :key="`bottom-${page}`" class="pagination-btn"
                        :class="{ active: page === currentPage }" @click="currentPage = page">
                        {{ page }}
                    </button>

                    <button class="pagination-btn" :disabled="currentPage === totalPages" @click="currentPage++">
                        &gt;
                    </button>
                    <button class="pagination-btn" :disabled="currentPage === totalPages"
                        @click="currentPage = totalPages">
                        &raquo;
                    </button>
                </div>
            </ul>
        </div>
    </div>
</template>

<script>
import { mapMutations } from "vuex";
import ModalHeader from "./components/ModalHeader/ModalHeader.vue";
import { BButton } from "bootstrap-vue";
import ArrowDownIcon from "@/@core/assets/icons/arrow-down.svg";
import CalendarIcon from "@/@core/assets/icons/calendar-icon.svg";
import CirclePauseIcon from "@/@core/assets/icons/circle-pause-icon.svg";
import TimeIcon from "@core/assets/icons/time-icon.svg";
import ProfileIcon from "@core/assets/icons/profile-icon.svg";
import DateTimePicker from "@/components/components-code/date-time-picker/DatePickerFull.vue";
import SelectModal from "@/views/pages/operator/components/SelectModal/SelectModal.vue";
import moment from "moment";
import ItemList from "./components/ItemList/ItemList.vue";
import ItemParameter from "./components/ItemParameter/ItemParameter.vue";
import NotFound from "../NotFound.vue";

export default {
    components: {
        ModalHeader,
        ArrowDownIcon,
        CalendarIcon,
        CirclePauseIcon,
        TimeIcon,
        ProfileIcon,
        BButton,
        DateTimePicker,
        SelectModal,
        ItemList,
        ItemParameter,
        NotFound
    },
    props: {
        isFilter: {
            type: Boolean,
            default: false
        },
        operationId: {
            type: Number,
            default: null
        },
        statusList: {
            type: Array,
            default: () => { }
        },
        peopleList: {
            type: Array,
            default: () => { }
        },
        shiftList: {
            type: Array,
            default: () => { }
        },
        close: {
            type: Function,
            default: () => { }
        },
        isFromOrders: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            currentTab: "stops",
            datepicker: null,
            selectModal: {
                selectedId: null,
                open: false,
                title: "",
                data: []
            },
            list: [],
            data: [],
            itemsPerPage: 50,
            currentPage: 1
        };
    },
    computed: {
        totalPages() {
            if (this.currentTab === 'stops') return 1;
            return Math.ceil(this.list.length / this.itemsPerPage);
        },
        paginatedList() {
            if (this.currentTab === 'stops') return this.list;
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.list.slice(start, end);
        },
        visiblePages() {
            const pages = [];
            const maxVisible = 5;
            let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
            let end = Math.min(this.totalPages, start + maxVisible - 1);

            if (end - start < maxVisible - 1) {
                start = Math.max(1, end - maxVisible + 1);
            }

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            return pages;
        }
    },
    mounted() {
        this.addOpenAnimation();
    },
    methods: {
        ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
        addOpenAnimation() {
            const modal = document.querySelector(".history-modal-container");
            modal.classList.add("animation-open-modal");

            setTimeout(() => {
                modal.classList.remove("animation-open-modal");
            }, 1000);
        },
        closeModal() {
            const modal = document.querySelector(".history-modal-container");
            modal.classList.add("animation-close-modal");
            modal.classList.add("closed-modal");

            setTimeout(() => this.$emit("close"), 1000);
        },
        showDates() {
            const currentYear = new Date().getFullYear();
            const firstDayPreviousYear = new Date(currentYear - 1, 0, 1);
            const today = new Date(new Date().setDate(new Date().getDate() + 1));

            return [firstDayPreviousYear, today];
        },
        blockDates(date) {
            const currentDate = moment(moment().format("YYYY-MM-DD"));
            const inspected = moment(moment(new Date(date)).format("YYYY-MM-DD"));

            return inspected > currentDate;
        },
        setWeekDays() {
            const weekdays = [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday"
            ];

            return weekdays.map((day) => ({
                name: this.$t(`Datepicker.Weekdays.Full.${day}`),
                short: this.$t(`Datepicker.Weekdays.Short.${day}`)
            }));
        },
        setMonths() {
            const months = [
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December"
            ];

            return months.map((month) => ({
                name: this.$t(`Datepicker.Months.Full.${month}`),
                short: this.$t(`Datepicker.Months.Short.${month}`)
            }));
        },
        handleDatepickerChange(date) {
            this.getHistoryList();
        },
        formatRangeDate(firstDate, secondDate) {
            return `${moment(firstDate).format("DD-MM-YY")} ${this.$t("Operator.Until")} ${moment(secondDate).format("DD-MM-YY")}`;
        },
        handleSelectModal(type) {
            const filterType = this.data.find((e) => e.field === type)?.value || null;
            switch (type) {
                case "shift":
                    this.selectModal = {
                        selectedId: filterType,
                        open: true,
                        title: "Shift",
                        data: this.shiftList
                    };
                    break;
                case "person":
                    this.selectModal = {
                        selectedId: filterType,
                        open: true,
                        title: "Person",
                        data: this.peopleList
                    };
                    break;
                case "status":
                    this.selectModal = {
                        selectedId: filterType,
                        open: true,
                        title: "Status",
                        data: this.statusList
                    };

                    break;

                default:
                    break;
            }
        },
        clearFilters() {
            this.datepicker = null;
            this.selectModal = {
                selectedId: null,
                open: false,
                title: "",
                data: []
            };
            this.data = [];
        },
        handleSelectModalClick(value) {
            const field = String(value.title).toLowerCase();
            const newItems = value.items;

            const existingField = this.data.find((d) => d.field === field);

            if (existingField) {
                existingField.value = [...newItems];
            } else {
                this.data.push({ field, value: [...newItems] });
            }

            this.getHistoryList();
        },
        async getHistoryList() {
            this.UPDATE_FLAG_SHOW_OVERLAY(true);
            const getFilters = (type) => {
                return this.data.find((e) => e.field === type)?.value || [];
            };
            const resourceQuery = this.isFromOrders ? "" : `&ResourceId=${this.$router.currentRoute.params.resource_id || ""}`;

            if (this.currentTab === "stops") {
                await this.$http2
                    .get(
                        `/api/mysfc/operation-progress/history-stop?OperationId=${this.operationId}&StartDate=${this.datepicker ? moment(new Date(this.datepicker[0])).format("YYYY-MM-DD") : ""}&EndDate=${this.datepicker ? moment(new Date(this.datepicker[1])).format("YYYY-MM-DD") : ""}&ShiftId=${getFilters("shift")}&PersonGuid=${getFilters("person")}&StatusId=${getFilters("status")}${resourceQuery}`
                    )
                    .then((res) => {
                        this.list = res.data.data;
                    });
            }

            if (this.currentTab === "quantity") {
                await this.$http2
                    .get(
                        `/api/mysfc/operation-progress/history-quantity?OperationId=${this.operationId}&StartDate=${this.datepicker ? moment(new Date(this.datepicker[0])).format("YYYY-MM-DD") : ""}&EndDate=${this.datepicker ? moment(new Date(this.datepicker[1])).format("YYYY-MM-DD") : ""}&ShiftId=${getFilters("shift")}&PersonGuid=${getFilters("person")}${resourceQuery}`
                    )
                    .then((res) => {
                        this.list = res.data.data;
                    });
            }

            if (this.currentTab === "parameters") {
                const siteId = this.$cookies.get("userInfo")?.currentSite;
                const requests = [];

                if (this.operationId && siteId) {
                    requests.push(
                        this.$http2
                            .get(`/api/mysfc/operator/operation-progress?operationId=${this.operationId}&siteId=${siteId}`)
                            .catch(() => ({ data: { data: [] } }))
                    );
                }

                requests.push(
                    this.$http2
                        .get(
                            `/api/mysfc/operation-progress/history-quantity?OperationId=${this.operationId}&StartDate=${this.datepicker ? moment(new Date(this.datepicker[0])).format("YYYY-MM-DD") : ""}&EndDate=${this.datepicker ? moment(new Date(this.datepicker[1])).format("YYYY-MM-DD") : ""}&ShiftId=${getFilters("shift")}&PersonGuid=${getFilters("person")}${resourceQuery}`
                        )
                        .catch(() => ({ data: { data: [] } })),
                    this.$http2
                        .get(
                            `/api/mysfc/operation-progress/history-stop?OperationId=${this.operationId}&StartDate=${this.datepicker ? moment(new Date(this.datepicker[0])).format("YYYY-MM-DD") : ""}&EndDate=${this.datepicker ? moment(new Date(this.datepicker[1])).format("YYYY-MM-DD") : ""}&ShiftId=${getFilters("shift")}&PersonGuid=${getFilters("person")}&StatusId=${getFilters("status")}${resourceQuery}`
                        )
                        .catch(() => ({ data: { data: [] } }))
                );

                const responses = await Promise.all(requests);
                const allItems = responses.flatMap((res) => res?.data?.data || []);

                const seenIds = new Set();
                let uniqueWithParams = [];
                for (const item of allItems) {
                    if (item.parameters && item.parameters.length > 0 && !seenIds.has(item.id)) {
                        seenIds.add(item.id);
                        uniqueWithParams.push(item);
                    }
                }

                if (this.datepicker && this.datepicker[0] && this.datepicker[1]) {
                    const start = moment(new Date(this.datepicker[0])).startOf("day");
                    const end = moment(new Date(this.datepicker[1])).endOf("day");
                    uniqueWithParams = uniqueWithParams.filter((item) => {
                        const evt = moment(item.eventTime);
                        return evt.isBetween(start, end, null, "[]");
                    });
                }

                const personFilter = getFilters("person");
                if (personFilter && personFilter.length > 0) {
                    uniqueWithParams = uniqueWithParams.filter((item) => {
                        const userGuid = item.pointingUserGuid || item.userId;
                        return userGuid && personFilter.includes(String(userGuid));
                    });
                }

                uniqueWithParams.sort((a, b) => (b.id || 0) - (a.id || 0));
                this.list = uniqueWithParams;
            }
            this.UPDATE_FLAG_SHOW_OVERLAY(false);
        }
    },
    mounted() {
        this.getHistoryList();
    },
    watch: {
        currentTab() {
            this.clearFilters();
            this.currentPage = 1;
            this.getHistoryList();
        },
        list() {
            this.currentPage = 1;
        }
    }
};
</script>

<style>
body:has(.history-modal-container) {
    overflow: hidden;
}
</style>

<style lang="scss" scoped>
.history-modal-container {
    height: 100vh;
    width: 100vw;
    background: #fcfcfc;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
    display: flex;
    flex-direction: column;

    .filters-buttons {
        display: flex;
        gap: 16px;
        padding: 24px 29px 0;
    }

    .btn-filter {
        display: flex;
        gap: 8px;
        align-items: center;
        font-weight: 600 !important;
        font-size: 14px !important;
        line-height: 20px !important;
        letter-spacing: 0.175px;
        color: #974900 !important;
        background-color: #ffffff !important;
        border-radius: 5px;
        height: 38px !important;
        padding: 6px 12px;
        border: 1px solid #974900 !important;

        svg {
            height: 14px;
            width: 14px;
            fill: #974900;
        }

        &:hover {
            background-color: #ffede1 !important;
            box-shadow: none !important;
        }
    }

    &.closed-modal {
        top: 100vh;
    }

    &.animation-open-modal {
        animation: openModal 0.5s normal;

        @keyframes openModal {
            0% {
                top: 100vh;
            }

            100% {
                top: 0;
            }
        }
    }

    &.animation-close-modal {
        animation: closeModal 0.5s normal;

        @keyframes closeModal {
            0% {
                top: 0;
            }

            100% {
                top: 100vh;
            }
        }
    }

    .list-content {
        display: flex;
        flex-direction: column;
        gap: 16px;

        &.--has-filters {
            height: calc(100vh - 199px);
        }

        &::-webkit-scrollbar {
            width: 16px;
            /* width of the entire scrollbar */
        }

        &::-webkit-scrollbar-track {
            background: #fff;
        }

        &::-webkit-scrollbar-thumb {
            background-color: #cfc4be;
            /* color of the scroll thumb */
            border-radius: 20px;
            /* roundness of the scroll thumb */
            border: 4px solid #fff;
            height: 70px;
        }

        height: calc(100vh - 143px);
        overflow: auto;
        padding: 24px 29px;
        margin: 0;

        @media (max-width: 768px) and (min-width: 593px) {
            height: calc(100vh - 180px);
        }

        @media (max-width: 593px) {
            height: calc(100vh - 155px);
        }

        @media (max-width: 425px) {
            height: calc(100vh - 131px);
        }

        .pagination-container {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
            margin-bottom: 8px;

            .pagination-btn {
                min-width: 38px;
                height: 38px;
                padding: 8px 12px;
                display: flex;
                align-items: center;
                justify-content: center;
                border: 1px solid #cfc4be;
                border-radius: 5px;
                background: #ffffff;
                color: #4c4541;
                font-weight: 600;
                font-size: 16px;
                cursor: pointer;
                transition: all 0.2s;

                &:hover:not(:disabled) {
                    background: #ffede2;
                    border-color: #974900;
                }

                &.active {
                    background: #974900;
                    color: #ffffff;
                    border-color: #974900;
                }

                &:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }
            }

            @media (max-width: 425px) {
                gap: 4px;

                .pagination-btn {
                    min-width: 32px;
                    height: 32px;
                    padding: 6px 10px;
                    font-size: 14px;
                }
            }
        }
    }

    .footer-modal-select {
        display: flex;
        justify-content: flex-end;
        padding: 15.5px 29px;
        border-top: 1px solid #cfc4be;
        background: #fff;
        box-shadow:
            0px 3px 4px 0px rgba(0, 0, 0, 0.14),
            0px 3px 3px 0px rgba(0, 0, 0, 0.12),
            0px 1px 8px 0px rgba(0, 0, 0, 0.2);
    }
}
</style>
